import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IRootState, IRootThunkDispatch } from "../../redux/store";
import { Button, FormControlLabel, Grid, InputLabel, Radio, RadioGroup } from "@mui/material";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import styles from "./CleaningDetailImage.module.css";
import FormDialog from "../common/FormDialog";
import AvatarEditor from "react-avatar-editor";
import { isSuperAdmin } from "../../util/form/form";
import { OpenLoadingBarThunk } from "../../redux/common/isLoading/thunk";
import { axiosGet, axiosPostWithFormData } from "../../util/axios";
import {
  initCleaningDetailRoutePhotoThunk,
  initCleaningImageTypeListThunk,
} from "../../redux/cleaning/thunk";
import { CleanImageType, CleaningDetailImageList } from "../../redux/cleaning/state";
import axios from "axios";
import { DateTimePicker } from "@mui/x-date-pickers";
import moment from "moment";
import { OpenSnackBarWithResultFail } from "../../redux/common/snackBar/thunk";
import { ButtonTextGrid } from "../..";

function CleaningDetailImage({
  selectedCleaningId,
  selectedCleaningDate,
  handleClose,
  binId,
  contractId
}: {
  selectedCleaningId: number;
  selectedCleaningDate: moment.Moment | null | string;
  handleClose:()=>void
  binId?: number;
  contractId?: number;
}) {
  useEffect(() => {
    fetchCleaningType();
  }, [binId, contractId]);

  async function fetchCleaningType() {
    dispatch(OpenLoadingBarThunk(true));
    let url: string;
    if (contractId) {
      url = `/api/cleaning/imageType?contract_id=${contractId}`;
    } else if (binId) {
      url = `/api/cleaning/imageType?bin_id=${binId}`;
    } else {
      url = `/api/cleaning/imageType`;
    }
    const result = await axiosGet(url);
    if (result.data.success) {
      dispatch(initCleaningImageTypeListThunk(result.data.imageTypleList));
    }
    dispatch(OpenLoadingBarThunk(false));
  }

  const dispatch = useDispatch<IRootThunkDispatch>();
  const { cleaningDetailImageList, cleaningImageType } = useSelector(
    (state: IRootState) => state.cleaningState
  );

  const imageTypeOrderMap: Record<number, number> = (cleaningImageType && cleaningImageType.length > 0)
    ? (cleaningImageType as CleanImageType[]).reduce((acc: Record<number, number>, type: CleanImageType, index: number) => {
        acc[type.cleaning_image_type_id] = index;
        return acc;
      }, {})
    : {};

  const sortedImages = useMemo(() => {
    return [...cleaningDetailImageList].sort((a, b) => {
      const orderA = imageTypeOrderMap[a.image_type_id] ?? Number.MAX_SAFE_INTEGER;
      const orderB = imageTypeOrderMap[b.image_type_id] ?? Number.MAX_SAFE_INTEGER;
      return orderA - orderB;
    });
  }, [cleaningDetailImageList, imageTypeOrderMap]);

  const [rotation, setRotation] = useState<Record<number, number>>({});
  const { user } = useSelector((state: IRootState) => state.userAuth);
  const isSuperAdminRole = isSuperAdmin(user?.token?.role_id as number);

  const handleImageClick = (imageId: number) => {
    setRotation((prevRotation) => ({
      ...prevRotation,
      [imageId]: (prevRotation[imageId] || 0) + 90,
    }));
  };

  const [handleOpenRawImageDialog, setHandleOpenRawImageDialog] =
    useState<boolean>(false);
  const [clickPhotoIndex, setClickPhotoIndex] = useState<number>(0);
  const [photoTime, setPhotoTime] = useState<number | null>(null);
  const [useWatermark, setUseWatermark] = useState<string>("no");

  function handleClickPhoto(index: number) {
    setHandleOpenRawImageDialog(true);
    setClickPhotoIndex(index);
  }

  function handleCloseDialog() {
    setHandleOpenRawImageDialog(false);
  }

  async function handleChangeCleaningPhoto(
    event: ChangeEvent<HTMLInputElement>,
    type: string,
    cleaning_image_type_id:number
  ) {
    const files = event.target.files;

    if (useWatermark === "yes" && !photoTime){
            dispatch(OpenSnackBarWithResultFail('你未有挑選時間'))
            handleClose()
            return 
    }

    if (!files) return;
    const form = new FormData();
    form.append("cleaning_date", selectedCleaningDate?.toString() ?? "");
    form.append('cleaning_image_type_id',cleaning_image_type_id.toString())
    const file = files[0];
    form.append(type, file);
    form.append("useWatermark", useWatermark === "yes" ? "1" : "0");
    if (useWatermark === "yes" && photoTime) {
      form.append("timestamp", photoTime.toString());
    }
    

    dispatch(OpenLoadingBarThunk(true));
    const res = await axiosPostWithFormData(
      "api/cleaning/Photo/" + selectedCleaningId,
      form
    );
    if (res.data.success && res.data.record && res.data.record.length) {
      const cleaningPhotoListTmp: CleaningDetailImageList[] = JSON.parse(
        JSON.stringify(cleaningDetailImageList)
      );
      cleaningPhotoListTmp.push(res.data.record[0]);
      const sorted = [...cleaningPhotoListTmp].sort((a, b) => {
        const orderA = imageTypeOrderMap[a.image_type_id] ?? Number.MAX_SAFE_INTEGER;
        const orderB = imageTypeOrderMap[b.image_type_id] ?? Number.MAX_SAFE_INTEGER;
        return orderA - orderB;
      });

      dispatch(initCleaningDetailRoutePhotoThunk(sorted));
      setPhotoTime(null);
    }
    dispatch(OpenLoadingBarThunk(false));
  }

  async function handleDeleteCleaningPhoto(image: CleaningDetailImageList) {
    const token = localStorage.getItem("loginToken");
    dispatch(OpenLoadingBarThunk(true));
    const res = await axios.delete(
      process.env.REACT_APP_API +
        "api/cleaning/photo/" +
        image.cleaning_image_id,
      {
        headers: {
          ...{
            Authorization: `Bearer ${token}`,
          },
        },
      }
    );
    if (res.data.success) {
      let cleaningPhotoListTmp = JSON.parse(
        JSON.stringify(cleaningDetailImageList)
      );
      cleaningPhotoListTmp = cleaningPhotoListTmp.filter(
        (obj: CleaningDetailImageList) =>
          obj.cleaning_image_id !== image.cleaning_image_id
      );
      dispatch(initCleaningDetailRoutePhotoThunk(cleaningPhotoListTmp));
    }
    dispatch(OpenLoadingBarThunk(false));
  }

  function handlePhotoTimeChange(date: moment.Moment | null) {
    const unix = moment(date).local().valueOf();
    setPhotoTime(unix);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 30 }}>
      {isSuperAdminRole && (
        <Grid style={{ display: "flex", gap: 30, flexDirection: "row" }}>
          {cleaningImageType &&
            cleaningImageType.map((imageType) => (
              <Grid>
                <input
                  accept="image/*"
                  id={imageType.image_type_name_en}
                  type="file"
                  style={{ display: "none" }}
                  multiple
                  onChange={(e) =>
                    handleChangeCleaningPhoto(e, imageType.image_type_name_en,imageType.cleaning_image_type_id)
                  }
                />
                <label htmlFor={imageType.image_type_name_en}>
                  <Button variant="contained" component="span">
                    <ButtonTextGrid>
                      添加{imageType.image_type_name}相片
                    </ButtonTextGrid>
                  </Button>
                </label>
              </Grid>
            ))}
        </Grid>
      )}

      <Grid className={styles.effectivedateContainer}>
        <Grid style={{ marginBottom: 10 }}>
          <InputLabel>水印</InputLabel>
          <RadioGroup
            row
            value={useWatermark}
            onChange={(e) => setUseWatermark(e.target.value)}
          >
            <FormControlLabel value="yes" control={<Radio />} label="是" />
            <FormControlLabel value="no" control={<Radio />} label="否" />
          </RadioGroup>
        </Grid>

        {useWatermark === "yes" && (
          <>
            <InputLabel htmlFor="enquiry-date-picker">相片日期</InputLabel>
            <DateTimePicker
              onChange={(data) => handlePhotoTimeChange(data as moment.Moment)}
              value={moment.utc(photoTime)}
              sx={{ width: "100%" }}
              format="YYYY-MM-DD HH:mm"
              timeSteps={{ minutes: 1 }}
              ampm={false}
            />
          </>
        )}
      </Grid>

      <Grid style={{ display: "flex", flexDirection: "row", flexWrap: "wrap", gap: 20 }}>
        {sortedImages.map((image, index) => (         
          <Grid
          key={`cleaningDetailImageList-${image.cleaning_image_id}`}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 10,
            }}
          >
            <Grid style={{ fontSize: 30 }}>{image.image_type_name}</Grid>

            <Grid
              key={`photo-${index}`}
              onClick={() => handleClickPhoto(index)}
            >
              <Grid
                className={`${styles.DisplayPhotoContainer} ${styles.hover}`}
              >
                <AvatarEditor
                  image={`${process.env.REACT_APP_S3}${image.image_thumb}`}
                  color={[255, 255, 255, 0]} // RGBA
                  rotate={rotation[image.cleaning_image_id] || 0}
                  key={`cleanning-images-${index}`}
                  height={300}
                />
              </Grid>
            </Grid>

            <div onClick={() => handleImageClick(image.cleaning_image_id)}>
              <Button variant="contained">
                <ButtonTextGrid>
                  <RotateRightIcon />
                  <Grid>右轉45度</Grid>
                </ButtonTextGrid>
              </Button>
            </div>
            {isSuperAdminRole && (
              <Button
                variant="contained"
                onClick={() => handleDeleteCleaningPhoto(image)}
                style={{
                  marginTop: "10px",
                  marginBottom: "10px",
                }}
              >
                <ButtonTextGrid>
                  <Grid>刪除照片</Grid>
                </ButtonTextGrid>
              </Button>
            )}
          </Grid>
        ))}
      </Grid>

      {sortedImages[clickPhotoIndex] ? (
        <FormDialog
          title="詳細情況"
          open={handleOpenRawImageDialog}
          handleClose={handleCloseDialog}
          size="lg"
        >
          <img
            src={`${process.env.REACT_APP_S3}${sortedImages[clickPhotoIndex].image}`}
          ></img>
        </FormDialog>
      ) : (
        <></>
      )}
    </div>
  );
}

export default CleaningDetailImage;
